import { FC, useCallback, MouseEvent, useEffect, useState } from 'react'
import { produce } from 'immer'
import ReactDataSheet from 'react-datasheet'
import { WritableDraft } from 'immer/dist/internal'

import hearingMessage from '../../../data/hearing-message.json'
import {
  SupplierHearingHeader,
  BuildingInformation,
  ErrorMessage,
  ConfirmDialog,
  ErrorMessages
} from '../../../components/parts'
import { BuildingDecidedBySupplirFooter } from './BuildingDecidedBySupplierFooter'
import { BuildingDecidedBySupplierGrid } from './BuildingDecidedBySupplierGrid'
import { AdditionalBuildingGrid } from './AdditionalBuildingGrid'
import { useAccessibleAuthority, useLeaveGuard, usePageTitle } from '../../../components/hooks'
import {
  CommonActions
  , ConfirmReplyActions
  , useAppDispatch, useAppSelector
} from '../../../stores'
import { GetBuildingItemReqParams, GetHearingQuantityListRes, WorkQuantity } from '@gateway.Supplier'
import { GetSupplierHearingWorkQuantityListGateway } from '../../../gateway/supplier/GetSupplierHearingWorkQuantityListGateway'
import { PutSupplierHearingWorkQuantityListGateway } from '../../../gateway/supplier/PutSupplierHearingWorkQuantityListGateway'

import './BuildingDecidedBySupplier.css'
import { Authority } from '../../../gateway/system/Authority'
import { MessageProvider } from '../../../services'
import { QuotationRequestBuildingGrid } from '../quotation-request-building/QuotationRequestBuildingGrid'
import { BuildingInfoGet } from '@gateway.Building'

const MONTHS = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3]
const YEARS = 6

export type CellValue = number | null

export interface GridElement
  extends ReactDataSheet.Cell<GridElement, CellValue> {
  value: CellValue
}

export interface GridDataSate {
  grid: GridElement[][]
  workQuantityList: WorkQuantity[]
  workQuantityListLastMonth: WorkQuantity[]
  displayModes: boolean[]
}

const GRID_DATA_STATE: GridDataSate = {
  grid: [],
  workQuantityList: [],
  workQuantityListLastMonth: [],
  displayModes: []
}

const GET_BUILDING_ITEM_REQ_PARAMS: GetBuildingItemReqParams = {
  simulationYm: '',
  procurementNo: '',
  subNo: '',
  branchCd: '',
  expenseCd: '',
  expenseSeq: '',
  supplierCd: '',
  supplierBranchCd: ''
}

const HEARING_QUANTITY_LIST_RES: GetHearingQuantityListRes = {
  simulationYm: '',
  supplierCd: '',
  supplierBranchCd: '',
  factoryCd: '',
  expenseCd: '',
  targetHearingKbn: null,
  addStsCd: '',
  addStsCdBeforeChange: null,
  addStsCdAfterChange: null,
  addSts: '',
  workQuantityList: []
}

export const BuildingDecidedBySupplier: FC = () => {
  useAccessibleAuthority(Authority.Builder, Authority.Material, Authority.Branch, Authority.Supplier)

  usePageTitle('大成決定/大成見積中物件')

  const dispatch = useAppDispatch()

  const regStatus = useAppSelector((state) => state.confirmreply.regStatus)

  const {
    replyYm,
    simulationYm,
    supplierCd,
    factoryCd,
    expenseCd,
    expense,
    supplierNm,
    branchCd,
    branchNm,
    factoryNm,
    supplierBranchCd,
    supplierBranchNm
  } = useAppSelector((state) => state.confirmreply.confirmReplyInfo)

  const replyStsCd = useAppSelector((state) => state.confirmreply.replyStsCd)
  const userProp = useAppSelector((state) => state.common.userProp)
  const displayStartYear = useAppSelector((state) => state.common.displayStartYear)

  const [displayMode, setDisplayMode] = useState<'0' | '1'>('1')
  const [confirmDialogSignInShow, setConfirmDialogSignInShow] = useState(false)
  const [confirmDialogTempSaveShow, setConfirmDialogTempSaveShow] = useState(false)
  const [notificationMessages, setNotificationMessages] = useState<ErrorMessage[]>([])
  const [errorMessages, setErrorMessages] = useState<ErrorMessage[]>([])

  const [decidedHearingQuantityListRes, setDecidedHearingQuantityListRes] =
    useState<GetHearingQuantityListRes>(HEARING_QUANTITY_LIST_RES)
  const [additionalHearingQuantityListRes, setAdditionalHearingQuantityListRes] =
    useState<GetHearingQuantityListRes>(HEARING_QUANTITY_LIST_RES)
  const [quotationRequestHearingQuantityListRes, setQuotationRequestHearingQuantityListRes] =
    useState<GetHearingQuantityListRes>(HEARING_QUANTITY_LIST_RES)
  const [decidedHearingQuantityListResLastMonth, setDecidedHearingQuantityListResLastMonth] =
    useState<GetHearingQuantityListRes>(HEARING_QUANTITY_LIST_RES)
  const [additionalHearingQuantityListResLastMonth, setAdditionalHearingQuantityListResLastMonth] =
    useState<GetHearingQuantityListRes>(HEARING_QUANTITY_LIST_RES)
  const [quotationRequestHearingQuantityListResLastMonth, setQuotationRequestHearingQuantityListResLastMonth] =
    useState<GetHearingQuantityListRes>(HEARING_QUANTITY_LIST_RES)
  const [decidedBuildingGridDataSate, setDecidedBuildingGridDataSate] = useState(GRID_DATA_STATE)
  const [additionalBuildingGridDataSate, setAdditionalBuildingGridDataSate] = useState(GRID_DATA_STATE)
  const [quotationRequestBuildingGridDataSate, setQuotationRequestBuildingGridDataSate] = useState(GRID_DATA_STATE)
  const [userAuthority, setUserAuthority] = useState<number>(Authority.None)
  const [buildingInfoParams, setBuildingInfoParams] =
  useState<GetBuildingItemReqParams>(GET_BUILDING_ITEM_REQ_PARAMS)
  const [buildingInformationWindowShow, setBuildingInformationWindowShow] = useState(false)
  const [isAdditionalBuildingGridShow, setIsAdditionalBuildingGridShow] = useState(false)
  // 変更検知
  const [isChanged, setIsChanged] = useState(false)

  // 費目リスト
  const initialData = useAppSelector((x) => x.common.initialData)
  const codeNames = initialData.codeNames('HI01')

  // 過去月の判定フラグ
  const date = new Date()
  const currentYear = date.getFullYear()
  const currentMonth = date.getMonth() + 1
  const simulatedYear = Number(simulationYm.substring(0, 4))
  const simulatedMonth = Number(simulationYm.substring(4, simulationYm.length))
  const lastMonthFlg = simulatedYear < currentYear || (simulatedYear === currentYear && simulatedMonth < currentMonth)

  // 過去月または(業者ユーザかつ回答済)または確認済または(支店ユーザかつ自支店以外)の場合、入力項目を非活性にする
  const nonCtrlFlg =
    lastMonthFlg ||
    (userProp?.authority === Authority.Supplier && replyStsCd === '5') ||
    replyStsCd === '7' ||
    (userProp?.authority === Authority.Branch && userProp?.branchCode !== branchCd)

  const [, resetLeaveGuard] = useLeaveGuard([isChanged], '内容が編集中です。このページを離れますか？')

  // #region グリッドコンポーネントのHandler
  // 行追加ボタン押下
  const handleOnAddRowBtnClick = (
    _event: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>
  ) => {
    setIsChanged(true)
    setAdditionalBuildingGridDataSate(
      produce((draft) => {
        const girdCell: GridElement[] = []
        const monthValArr: number[] = []

        // 当月以前のセルを非活性に設定する
        for (let fy = 0; fy < YEARS; fy++) {
          for (let m = 0; m < MONTHS.length; m++) {
            girdCell.push({
              value: null,
              readOnly: onDataModifiableFn((fy * 12 + m), simulationYm, displayStartYear, userProp?.authority)
            })
            monthValArr.push(0)
          }
        }

        draft.grid.splice(draft.grid.length - 1, 0, girdCell)

        // 労務数(新規行)
        const workQuantityObj: WorkQuantity = {
          branchCd: '',
          procurementNo: '000000000',
          subNo: '00',
          expenseSeq: '00',
          workerKbn: '0', // その他
          // (業者追加物件)
          hearingKbn: '2',
          evaluation: '',
          constructionNm: '',
          constructionNmManual: '',
          otherCompany: '',
          constructionPartCd: null,
          processStsCd: null,
          delayLevelCd: null,
          newAddFlg: 1,
          notApplicableFlg: 0,
          hiddenFlg: 0,
          notDelFlg: 0,
          lostOrderFlg: 0,
          workQuantityByMonth: monthValArr,
          updDtm: null,
          delFlg: 0,
          decisionKbn: null,
          buildingDiscloseFlg: null,
          completedFlg: null,
          remark: null,
          count: null,
          usedAsCopySource: 0,
          isCopied: 0
        }

        draft.workQuantityList.push(workQuantityObj)
        draft.displayModes.push(true)
      })
    )
  }

  // 業者決定物件非表示チェックボックス変更
  const handleOnDecidedDisplayCheckboxChange = (
    selectedRowIndex: number,
    selected: boolean
  ) => {
    setIsChanged(true)
    setDecidedBuildingGridDataSate(
      produce((draft) => {
        draft.workQuantityList[selectedRowIndex].hiddenFlg = Number(selected)
      })
    )
  }

  // 業者追加物件非表示チェックボックス変更
  const handleOnAdditionalDisplayCheckboxChange = (
    selectedRowIndex: number,
    selected: boolean
  ) => {
    setIsChanged(true)
    setAdditionalBuildingGridDataSate(
      produce((draft) => {
        draft.workQuantityList[selectedRowIndex].hiddenFlg = Number(selected)
      })
    )
  }

  // 見積依頼物件非表示チェックボックス押下
  const handleOnQuotationRequestDisplayCheckboxChange = (
    selectedRowIndex: number,
    selected: boolean
  ) => {
    setIsChanged(true)
    setQuotationRequestBuildingGridDataSate(
      produce((draft) => {
        draft.workQuantityList[selectedRowIndex].hiddenFlg = Number(selected)
      })
    )
  }

  // 業者決定物件対象外チェックボックス変更
  const handleOnDecidedApplicabCheckboxChange = (
    selectedRowIndex: number,
    selected: boolean
  ) => {
    setIsChanged(true)
    setDecidedBuildingGridDataSate(
      produce((draft) => {
        draft.workQuantityList[selectedRowIndex].notApplicableFlg = Number(selected)
      })
    )
  }

  // 業者追加物件対象外チェックボックス変更
  const handleOnAdditionalApplicabCheckboxChange = (
    selectedRowIndex: number,
    selected: boolean
  ) => {
    setIsChanged(true)
    setAdditionalBuildingGridDataSate(
      produce((draft) => {
        draft.workQuantityList[selectedRowIndex].notApplicableFlg = Number(selected)
      })
    )
  }

  // 業者決定物件施工部位変更
  const handleOnConstPartDropDownChange = (val: string, rowNo: number) => {
    setIsChanged(true)
    setDecidedBuildingGridDataSate(
      produce((draft) => {
        draft.workQuantityList[rowNo].constructionPartCd = val
        if (draft.workQuantityList[rowNo].count === 2) {
          draft.workQuantityList[rowNo + 1].constructionPartCd = val
        }
      })
    )
  }

  // 業者決定物件工程状況変更
  const handleOnPrssStsDropDownChange = (val: string, rowNo: number) => {
    setIsChanged(true)
    setDecidedBuildingGridDataSate(
      produce((draft) => {
        draft.workQuantityList[rowNo].processStsCd = val
        if (draft.workQuantityList[rowNo].count === 2) {
          draft.workQuantityList[rowNo + 1].processStsCd = val
        }
      })
    )
  }

  // 業者決定物件遅延程度変更
  const handleOnDelayLevelCdDropDownChange = (val: string, rowNo: number) => {
    setIsChanged(true)
    setDecidedBuildingGridDataSate(
      produce((draft) => {
        draft.workQuantityList[rowNo].delayLevelCd = val
        if (draft.workQuantityList[rowNo].count === 2) {
          draft.workQuantityList[rowNo + 1].delayLevelCd = val
        }
      })
    )
  }

  // 直手/応援区分変更
  const handleOnWorkerKbnDropDownChange = (val: string, rowNo: number) => {
    setIsChanged(true)
    setDecidedBuildingGridDataSate(
      produce((draft) => {
        draft.workQuantityList[rowNo].workerKbn = val
        decidedBuildingGridDataSate.workQuantityList.forEach((obj, rowIndex) => {
          const lastRowIndex = decidedBuildingGridDataSate.grid.length - 1
          setTotalRowData(draft, rowIndex, lastRowIndex, true)
        })
      })
    )
  }

  // 業者追加物件削除チェックボックス押下時
  const handleOnDeleteCheckboxChange = (
    selectedRowIndex: number,
    selected: boolean
  ) => {
    setIsChanged(true)
    setAdditionalBuildingGridDataSate(
      produce((draft) => {
        draft.workQuantityList[selectedRowIndex].delFlg = Number(selected)
      })
    )
  }

  // 業者追加物件支店変更
  const handleOnBranchDropDownChange = (val: string, rowNo: number) => {
    setIsChanged(true)
    setAdditionalBuildingGridDataSate(
      produce((draft) => {
        draft.workQuantityList[rowNo].branchCd = val
      })
    )
  }

  // 業者追加物件建物情報変更
  const handleOnAddBuildingInfoChange = (val: BuildingInfoGet | null, rowNo: number) => {
    if (val?.procurementNo == null) return

    // 業者決定物件および見積依頼物件に業者追加物件と同じ物件が存在していないかをチェック
    const [procurementNo, subNo] = val?.procurementNo.split('-') ?? []
    if (decidedBuildingGridDataSate.workQuantityList.some((decidedBuilding) => decidedBuilding.procurementNo === procurementNo && decidedBuilding.subNo === subNo)) {
      setErrorMessages([
        {
          id: 'E022',
          content: MessageProvider.provide('E022', '選択した物件が大成決定物件に存在する', '追加')
        }
      ])
      return
    }
    if (quotationRequestBuildingGridDataSate.workQuantityList.some((quotationRequestBuilding) => quotationRequestBuilding.procurementNo === procurementNo && quotationRequestBuilding.subNo === subNo)) {
      setErrorMessages([
        {
          id: 'E022',
          content: MessageProvider.provide('E022', '選択した物件が大成見積中物件に存在する', '追加')
        }
      ])
      return
    }

    setIsChanged(true)
    setAdditionalBuildingGridDataSate(
      produce((draft) => {
        draft.workQuantityList[rowNo].constructionNmManual = val?.constructionNm ?? null
        draft.workQuantityList[rowNo].remark = val?.procurementNo ?? null
        draft.workQuantityList[rowNo].branchCd = val?.branchCd ?? ''
      })
    )
  }

  // 見積依頼物件備考の変更時
  const handleOnRemarkInputChange = (val: string, rowNo: number) => {
    setIsChanged(true)
    setQuotationRequestBuildingGridDataSate(
      produce((draft) => {
        draft.workQuantityList[rowNo].remark = val
      })
    )
  }

  // 業者決定物件セルの値変更時
  const handleDecidedCellsChanged = (
    arrayOfChanges: ReactDataSheet.CellsChangedArgs<GridElement, CellValue>
  ) => {
    setIsChanged(true)
    if (isNaN(Number(arrayOfChanges[0]?.value))) return

    setDecidedBuildingGridDataSate(
      produce((draft) => {
        arrayOfChanges.forEach(({ row, col, value }) => {
          const preCellVal = Number(draft.grid[row][col].value)
          const currentCellVal = Number(value)
          draft.grid[row][col].value = value

          draft.workQuantityList[row].workQuantityByMonth[col] = currentCellVal
          const preCellTotalVal = Number(
            draft.grid[draft.grid.length - 1][col].value
          )
          // 直手小計
          if (draft.workQuantityList[row].workerKbn === '1') {
            const preDecisionCellTotalVal = Number(
              draft.grid[draft.grid.length - 3][col].value
            )
            const currentDecisionCellTotalVal =
             preDecisionCellTotalVal - preCellVal + currentCellVal
            draft.grid[draft.grid.length - 3][col].value =
             currentDecisionCellTotalVal
          }
          // 応援小計
          if (draft.workQuantityList[row].workerKbn === '2') {
            const preExpectationCellTotalVal = Number(
              draft.grid[draft.grid.length - 2][col].value
            )
            const currentExpectationCellTotalVal =
            preExpectationCellTotalVal - preCellVal + currentCellVal
            draft.grid[draft.grid.length - 2][col].value =
             currentExpectationCellTotalVal
          }
          // 総合計
          const currentCellTotalVal =
            preCellTotalVal - preCellVal + currentCellVal
          draft.grid[draft.grid.length - 1][col].value =
            currentCellTotalVal
        })
      })
    )
  }

  // 業者追加物件セルの値変更時
  const handleAdditionalCellsChanged = (
    arrayOfChanges: ReactDataSheet.CellsChangedArgs<GridElement, CellValue>
  ) => {
    setIsChanged(true)
    if (isNaN(Number(arrayOfChanges[0]?.value))) return

    setAdditionalBuildingGridDataSate(
      produce((draft) => {
        arrayOfChanges.forEach(({ row, col, value }) => {
          const preCellVal = Number(draft.grid[row][col].value)
          const currentCellVal = Number(value)
          draft.grid[row][col].value = value

          draft.workQuantityList[row].workQuantityByMonth[col] = currentCellVal
          const preCellTotalVal = Number(
            draft.grid[draft.grid.length - 1][col].value
          )
          const currentCellTotalVal =
              preCellTotalVal - preCellVal + currentCellVal
          draft.grid[draft.grid.length - 1][col].value =
              currentCellTotalVal
        })
      })
    )
  }

  // 見積依頼物件セルの値変更時
  const handleQuotationRequestCellsChanged = (
    arrayOfChanges: ReactDataSheet.CellsChangedArgs<GridElement, CellValue>
  ) => {
    setIsChanged(true)
    if (isNaN(Number(arrayOfChanges[0]?.value))) return

    setQuotationRequestBuildingGridDataSate(
      produce((draft) => {
        arrayOfChanges.forEach(({ row, col, value }) => {
          const preCellVal = Number(draft.grid[row][col].value)
          const currentCellVal = Number(value)
          draft.grid[row][col].value = value

          draft.workQuantityList[row].workQuantityByMonth[col] = currentCellVal
          const preCellTotalVal = Number(
            draft.grid[draft.grid.length - 1][col].value
          )
          const currentCellTotalVal =
            preCellTotalVal - preCellVal + currentCellVal
          draft.grid[draft.grid.length - 1][col].value =
            currentCellTotalVal
        })
      })
    )
  }

  // 詳細ボタン押下
  const handleBuildingInfoBtnClick = (workQuantity: WorkQuantity) => {
    const { procurementNo, subNo, branchCd, expenseSeq } = workQuantity
    setBuildingInfoParams(
      produce((draft) => {
        draft.simulationYm = simulationYm
        draft.procurementNo = procurementNo
        draft.subNo = subNo
        draft.branchCd = branchCd
        draft.expenseCd = expenseCd
        draft.expenseSeq = expenseSeq
        draft.supplierCd = supplierCd
        draft.supplierBranchCd = supplierBranchCd
      })
    )

    setBuildingInformationWindowShow(!buildingInformationWindowShow)
  }

  // 複製ボタン押下
  const handleDuplicationBtnClick = (workQuantity: WorkQuantity, rowNo: number) => {
    setIsChanged(true)
    setDecidedBuildingGridDataSate(
      produce((draft) => {
        const girdCell: GridElement[] = []
        const monthValArr: number[] = []

        // 当月以前のセルを非活性に設定する
        for (let fy = 0; fy < YEARS; fy++) {
          for (let m = 0; m < MONTHS.length; m++) {
            girdCell.push({
              value: null,
              readOnly: onDataModifiableFn((fy * 12 + m), simulationYm, displayStartYear, userProp?.authority)
            })
            monthValArr.push(0)
          }
        }

        // 複製ボタンの活性非活性を決定するために、押下元をカウントアップする
        draft.workQuantityList[rowNo].count = 2
        // 今、画面上で複製元として使用されたことを示す
        draft.workQuantityList[rowNo].usedAsCopySource = 1
        // 押下した行の後続に挿入する（カレンダ部）
        draft.grid.splice(rowNo + 1, 0, girdCell)

        // 労務数(新規行)
        const workQuantityObj: WorkQuantity = {
          branchCd: workQuantity.branchCd,
          procurementNo: workQuantity.procurementNo,
          subNo: workQuantity.subNo,
          expenseSeq: workQuantity.expenseSeq,
          workerKbn: workQuantity.workerKbn === '1' ? '2' : '1',
          hearingKbn: '1', // 業者決定物件
          evaluation: '',
          constructionNm: '', // 追加行であることを示すため空の工事名を設定する
          constructionNmManual: '',
          otherCompany: '',
          constructionPartCd: workQuantity.constructionPartCd,
          processStsCd: workQuantity.processStsCd,
          delayLevelCd: workQuantity.delayLevelCd,
          newAddFlg: 1,
          notApplicableFlg: 0,
          hiddenFlg: 0,
          notDelFlg: 0,
          lostOrderFlg: 0,
          workQuantityByMonth: monthValArr,
          updDtm: null,
          delFlg: 0,
          decisionKbn: null,
          buildingDiscloseFlg: null,
          completedFlg: null,
          remark: null,
          count: 2, // 直手と応援双方が存在することから2件ある
          usedAsCopySource: 0,
          isCopied: 1
        }

        // 押下した行の後続に挿入する（カレンダ部以外）
        draft.workQuantityList.splice(rowNo + 1, 0, workQuantityObj)
        // 当該新規行は表示モードとする
        draft.displayModes.splice(rowNo + 1, 0, true)
      })
    )
  }

  // 複製取消ボタン押下
  const handleUndoDuplicationBtnClick = (workQuantity: WorkQuantity, rowNo: number) => {
    setIsChanged(true)
    setDecidedBuildingGridDataSate(
      produce((draft) => {
        // 複製ボタンの活性非活性を決定するために、押下元をカウントダウンする
        draft.workQuantityList[rowNo - 1].count = 1
        // 押下した行を削除する（カレンダ部）
        draft.grid.splice(rowNo, 1)
        // 押下した行を削除する（カレンダ部以外）
        draft.workQuantityList.splice(rowNo, 1)
        // 押下した行の表示モードを削除する
        draft.displayModes.splice(rowNo, 1)
        // 合計行の再計算を行う
        draft.workQuantityList.forEach((obj, rowIndex) => {
          const lastRowIndex = draft.grid.length - 1
          setTotalRowData(draft, rowIndex, lastRowIndex, true)
        })
      })
    )
  }

  // #endregion

  // 物件詳細閉じるボタン押下
  const handleOnCloseBuildingInfoWindow = () => {
    setBuildingInformationWindowShow(!buildingInformationWindowShow)
  }

  // 全表示・非表示ボタン押下
  const handleOnHiddenBtnClick = (
    _event: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>
  ) => {
    if (displayMode === '0') {
      // 非表示モードの場合
      setDisplayMode('1')

      setDecidedBuildingGridDataSate(
        produce((draft) => {
          decidedBuildingGridDataSate.workQuantityList.forEach((obj, rowIndex) => {
            const isDisplay = obj.hiddenFlg === 0 && obj.lostOrderFlg === 0
            const lastRowIndex = decidedBuildingGridDataSate.grid.length - 1
            setTotalRowData(draft, rowIndex, lastRowIndex, true)

            draft.displayModes[rowIndex] = isDisplay
          })
        })
      )

      setAdditionalBuildingGridDataSate(
        produce((draft) => {
          additionalBuildingGridDataSate.workQuantityList.forEach((obj, rowIndex) => {
            const isDisplay = obj.hiddenFlg === 0 && obj.lostOrderFlg === 0
            const lastRowIndex = additionalBuildingGridDataSate.grid.length - 1
            setTotalRowData(draft, rowIndex, lastRowIndex, false)

            draft.displayModes[rowIndex] = isDisplay
          })
        })
      )

      setQuotationRequestBuildingGridDataSate(
        produce((draft) => {
          quotationRequestBuildingGridDataSate.workQuantityList.forEach((obj, rowIndex) => {
            const isDisplay = obj.hiddenFlg === 0 && obj.lostOrderFlg === 0
            const lastRowIndex = quotationRequestBuildingGridDataSate.grid.length - 1
            setTotalRowData(draft, rowIndex, lastRowIndex, false)

            draft.displayModes[rowIndex] = isDisplay
          })
        })
      )
    }
    if (displayMode === '1') {
      // 全表示モードの場合
      setDisplayMode('0')

      setDecidedBuildingGridDataSate(
        produce((draft) => {
          decidedBuildingGridDataSate.workQuantityList.forEach((obj, rowIndex) => {
            const isDisplay = true
            const lastRowIndex = decidedBuildingGridDataSate.grid.length - 1
            setTotalRowData(draft, rowIndex, lastRowIndex, true)

            draft.displayModes[rowIndex] = isDisplay
          })
        })
      )

      setAdditionalBuildingGridDataSate(
        produce((draft) => {
          additionalBuildingGridDataSate.workQuantityList.forEach((obj, rowIndex) => {
            const isDisplay = true
            const lastRowIndex = additionalBuildingGridDataSate.grid.length - 1
            setTotalRowData(draft, rowIndex, lastRowIndex, false)

            draft.displayModes[rowIndex] = isDisplay
          })
        })
      )

      setQuotationRequestBuildingGridDataSate(
        produce((draft) => {
          quotationRequestBuildingGridDataSate.workQuantityList.forEach((obj, rowIndex) => {
            const isDisplay = true
            const lastRowIndex = quotationRequestBuildingGridDataSate.grid.length - 1
            setTotalRowData(draft, rowIndex, lastRowIndex, false)

            draft.displayModes[rowIndex] = isDisplay
          })
        })
      )
    }
  }

  // 登録ボタン押下
  const handleOnSaveBtnClick = (
    _event: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>
  ) => {
    setConfirmDialogSignInShow(true)
  }

  // 一時保存ボタン押下
  const handleOnTempSaveBtnClick = (
    _event: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>
  ) => {
    setConfirmDialogTempSaveShow(true)
  }

  // 業者追加物件に登録されている物件が決定物件及び見積依頼物件に存在するかチェックする
  const checkAdditionalBuildingExist = () => {
    let isExist = false
    additionalBuildingGridDataSate.workQuantityList.forEach((additional) => {
      if (additional.delFlg !== 1 && additional.remark !== null) {
        const [procurementNo, subNo] = additional.remark.split('-')

        const existsInDecided = decidedBuildingGridDataSate.workQuantityList.some(
          (decidedObj) =>
            decidedObj.procurementNo === procurementNo &&
            decidedObj.subNo === subNo
        )
        const existsInQuotation = quotationRequestBuildingGridDataSate.workQuantityList.some(
          (quotationObj) =>
            quotationObj.procurementNo === procurementNo &&
            quotationObj.subNo === subNo
        )
        if (existsInDecided || existsInQuotation) {
          isExist = true
        }
      }
    })
    return isExist
  }

  // 登録確認ダイアログOKボタン押下
  const dialogConfirmSignInBtn = () => {
    setConfirmDialogSignInShow(false)

    // 業者追加物件の存在チェック
    if (checkAdditionalBuildingExist()) {
      setErrorMessages([
        {
          id: 'E022',
          content: MessageProvider.provide('E022', '業者追加物件が大成決定物件もしくは大成見積中物件に存在する', '登録')
        }
      ])
      return
    }

    const newQuantityList = produce(decidedHearingQuantityListRes, (draft) => {
      draft.targetHearingKbn = ['1', '2', '3']
      draft.addStsCdBeforeChange = draft.addStsCd
      // 回答ステータスコードが"8"(事前調整)の場合は登録ステータスに "4"(事前調整) それ以外は "3"(登録済) を設定する
      draft.addStsCdAfterChange = replyStsCd === '8' ? '4' : '3';
      // 業者ヒアリング(労務数)-明細登録用に表示開始年度を追加する
      // 業者ヒアリング(労務数)-明細登録はリクエストの型を業者ヒアリング(労務数)-明細取得と共用しているため
      // 明細登録用に表示開始年度は一時追加のプロパティとして設定する
      (draft as any).displayStartYear = displayStartYear

      const workQuantityList: WorkQuantity[] = []
      // 業者決定物件
      decidedBuildingGridDataSate.workQuantityList.forEach((obj) => {
        // State.更新日時＝NULLかつState.削除フラグ=1(削除)の行は対象外とする
        if (!(obj.updDtm === null && obj.delFlg === 1)) {
          const workQuantity = Object.assign({}, obj)
          // 決定区分
          workQuantity.decisionKbn = ''
          // 備考
          workQuantity.remark = ''
          // 差分マーク
          workQuantity.diffMark = diff(decidedBuildingGridDataSate, obj)

          workQuantityList.push(workQuantity)
        }
      })

      // 業者追加物件
      additionalBuildingGridDataSate.workQuantityList.forEach((obj) => {
        // State.更新日時＝NULLかつState.削除フラグ=1(削除)の行は対象外とする
        if (!(obj.updDtm === null && obj.delFlg === 1)) {
          const workQuantity = Object.assign({}, obj)
          // 決定区分
          workQuantity.decisionKbn = ''
          // 備考
          // workQuantity.remark = ''
          // 差分マーク
          workQuantity.diffMark = diff(additionalBuildingGridDataSate, obj)

          workQuantityList.push(workQuantity)
        }
      })

      // 見積依頼物件
      quotationRequestBuildingGridDataSate.workQuantityList.forEach((obj) => {
        // State.更新日時＝NULLかつState.削除フラグ=1(削除)の行は対象外とする
        if (!(obj.updDtm === null && obj.delFlg === 1)) {
          const workQuantity = Object.assign({}, obj)
          // 決定区分
          workQuantity.decisionKbn = ''
          // 差分マーク
          workQuantity.diffMark = diff(quotationRequestBuildingGridDataSate, obj)

          workQuantityList.push(workQuantity)
        }
      })

      draft.workQuantityList = workQuantityList
    })

    dispatch(CommonActions.setIsLoading(true))
    PutSupplierHearingWorkQuantityListGateway.putHearingQuantityList(
      { data: newQuantityList }
    )
      .then((res) => {
        if (!res.error) {
          fetchHearingQuantityList()
          if (isChanged) {
            resetLeaveGuard()
          }
          setNotificationMessages([
            {
              id: 'I004',
              content: MessageProvider.provide('I004', '登録')
            }
          ])
        } else {
          if (Array.isArray(res.messages) && res.messages.length > 0) {
            const errorMessages = res.messages.map<ErrorMessage>((message) => {
              return {
                id: message.id,
                content: message.message
              }
            })
            setErrorMessages(errorMessages)
          }
        }
      })
      .finally(() => {
        dispatch(CommonActions.setIsLoading(false))
      })
  }

  // 一時保存確認ダイアログOKボタン押下
  const dialogConfirmTempSaveBtn = () => {
    setConfirmDialogTempSaveShow(false)

    // 業者追加物件の存在チェック
    if (checkAdditionalBuildingExist()) {
      setErrorMessages([
        {
          id: 'E022',
          content: MessageProvider.provide('E022', '業者追加物件が大成決定物件もしくは大成見積中物件に存在する', '登録')
        }
      ])
      return
    }

    const newQuantityList = produce(decidedHearingQuantityListRes, (draft) => {
      draft.targetHearingKbn = ['1', '2', '3']
      draft.addStsCdBeforeChange = draft.addStsCd
      // 回答ステータスコードが"8"(事前調整)の場合は登録ステータスに "4"(事前調整) それ以外は "2"(一時保存) を設定する
      draft.addStsCdAfterChange = replyStsCd === '8' ? '4' : '2';
      // 業者ヒアリング(労務数)-明細登録用に表示開始年度を追加する
      // 業者ヒアリング(労務数)-明細登録はリクエストの型を業者ヒアリング(労務数)-明細取得と共用しているため
      // 明細登録用に表示開始年度は一時追加のプロパティとして設定する
      (draft as any).displayStartYear = displayStartYear

      const workQuantityList: WorkQuantity[] = []
      // 業者決定物件
      decidedBuildingGridDataSate.workQuantityList.forEach((obj) => {
        // State.更新日時＝NULLかつState.削除フラグ=1(削除)の行は対象外とする
        if (!(obj.updDtm === null && obj.delFlg === 1)) {
          const workQuantity = Object.assign({}, obj)
          // 決定区分
          workQuantity.decisionKbn = ''
          workQuantityList.push(workQuantity)
        }
      })

      // 業者追加物件
      additionalBuildingGridDataSate.workQuantityList.forEach((obj) => {
        // State.更新日時＝NULLかつState.削除フラグ=1(削除)の行は対象外とする
        if (!(obj.updDtm === null && obj.delFlg === 1)) {
          const workQuantity = Object.assign({}, obj)
          // 決定区分
          workQuantity.decisionKbn = ''
          workQuantityList.push(workQuantity)
        }
      })

      // 見積依頼物件
      quotationRequestBuildingGridDataSate.workQuantityList.forEach((obj) => {
        // State.更新日時＝NULLかつState.削除フラグ=1(削除)の行は対象外とする
        if (!(obj.updDtm === null && obj.delFlg === 1)) {
          const workQuantity = Object.assign({}, obj)
          // 決定区分
          workQuantity.decisionKbn = ''
          workQuantityList.push(workQuantity)
        }
      })

      draft.workQuantityList = workQuantityList
    })

    dispatch(CommonActions.setIsLoading(true))
    PutSupplierHearingWorkQuantityListGateway.putHearingQuantityList(
      { data: newQuantityList }
    )
      .then((res) => {
        if (!res.error) {
          fetchHearingQuantityList()
          if (isChanged) {
            resetLeaveGuard()
          }
          setNotificationMessages([
            {
              id: 'I004',
              content: MessageProvider.provide('I004', '一時保存')
            }
          ])
        } else {
          if (Array.isArray(res.messages) && res.messages.length > 0) {
            const errorMessages = res.messages.map<ErrorMessage>((message) => {
              return {
                id: message.id,
                content: message.message
              }
            })
            setErrorMessages(errorMessages)
          }
        }
      })
      .finally(() => {
        dispatch(CommonActions.setIsLoading(false))
      })
  }

  // データ取得
  const fetchHearingQuantityList = useCallback(() => {
    dispatch(CommonActions.setIsLoading(true))
    // 業者決定物件
    GetSupplierHearingWorkQuantityListGateway.getHearingQuantityList(
      simulationYm,
      supplierCd,
      supplierBranchCd,
      factoryCd,
      expenseCd,
      // "1"(業者決定物件)
      '1',
      displayStartYear
    )
      .then((res) => {
        if (!res.error) {
          setDecidedHearingQuantityListRes(res.data ?? HEARING_QUANTITY_LIST_RES)
        } else {
          const messages = res.messages.map((item) => ({
            id: item.id,
            content: item.message
          }))
          setErrorMessages(messages)
        }
      })
      .finally(() => {
        dispatch(CommonActions.setIsLoading(false))
      })

    // 業者追加物件
    GetSupplierHearingWorkQuantityListGateway.getHearingQuantityList(
      simulationYm,
      supplierCd,
      supplierBranchCd,
      factoryCd,
      expenseCd,
      // "2"(業者追加物件)
      '2',
      displayStartYear
    )
      .then((res) => {
        if (!res.error) {
          setAdditionalHearingQuantityListRes(res.data ?? HEARING_QUANTITY_LIST_RES)
        } else {
          const messages = res.messages.map((item) => ({
            id: item.id,
            content: item.message
          }))
          setErrorMessages(messages)
        }
      })
      .finally(() => {
        dispatch(CommonActions.setIsLoading(false))
      })

    // 見積依頼物件
    GetSupplierHearingWorkQuantityListGateway.getHearingQuantityList(
      simulationYm,
      supplierCd,
      supplierBranchCd,
      factoryCd,
      expenseCd,
      '3', // 見積依頼物件,
      displayStartYear
    )
      .then((res) => {
        if (!res.error) {
          setQuotationRequestHearingQuantityListRes(res.data ?? HEARING_QUANTITY_LIST_RES)
        } else {
          const messages = res.messages.map((item) => ({
            id: item.id,
            content: item.message
          }))
          setErrorMessages(messages)
        }
      })
      .finally(() => {
        dispatch(CommonActions.setIsLoading(false))
      })

    // シミュレーション年月度の前月度データの取得
    const simulatedYear = Number(simulationYm.substring(0, 4))
    const simulatedMonth = Number(
      simulationYm.substring(4, simulationYm.length)
    )
    // 先月の取得
    const simulationYmLastMonth = getPreMonth(simulatedYear, simulatedMonth)

    // 業者決定物件
    GetSupplierHearingWorkQuantityListGateway.getHearingQuantityList(
      simulationYmLastMonth,
      supplierCd,
      supplierBranchCd,
      factoryCd,
      expenseCd,
      // "1"(業者決定物件)
      '1',
      displayStartYear
    )
      .then((res) => {
        if (!res.error) {
          setDecidedHearingQuantityListResLastMonth(res.data ?? HEARING_QUANTITY_LIST_RES)
        } else {
          const messages = res.messages.map((item) => ({
            id: item.id,
            content: item.message
          }))
          setErrorMessages(messages)
        }
      })
      .finally(() => {
        dispatch(CommonActions.setIsLoading(false))
      })

    // 業者追加物件
    GetSupplierHearingWorkQuantityListGateway.getHearingQuantityList(
      simulationYmLastMonth,
      supplierCd,
      supplierBranchCd,
      factoryCd,
      expenseCd,
      // "2"(業者追加物件)
      '2',
      displayStartYear
    )
      .then((res) => {
        if (!res.error) {
          setAdditionalHearingQuantityListResLastMonth(res.data ?? HEARING_QUANTITY_LIST_RES)
        } else {
          const messages = res.messages.map((item) => ({
            id: item.id,
            content: item.message
          }))
          setErrorMessages(messages)
        }
      })
      .finally(() => {
        dispatch(CommonActions.setIsLoading(false))
      })

    // 見積依頼物件
    GetSupplierHearingWorkQuantityListGateway.getHearingQuantityList(
      simulationYmLastMonth,
      supplierCd,
      supplierBranchCd,
      factoryCd,
      expenseCd,
      '3', // 見積依頼物件
      displayStartYear
    )
      .then((res) => {
        if (!res.error) {
          setQuotationRequestHearingQuantityListResLastMonth(res.data ?? HEARING_QUANTITY_LIST_RES)
        } else {
          const messages = res.messages.map((item) => ({
            id: item.id,
            content: item.message
          }))
          setErrorMessages(messages)
        }
      })
      .finally(() => {
        dispatch(CommonActions.setIsLoading(false))
      })
  }, [expenseCd, factoryCd, simulationYm, supplierCd])

  // 差分情報の抽出
  const diff = (gridData: GridDataSate, workQuantity: WorkQuantity) => {
    let diffList: string[] = []
    const lastMonth = gridData.workQuantityListLastMonth.find((obj) =>
      obj.procurementNo === workQuantity.procurementNo && obj.subNo === workQuantity.subNo && obj.expenseSeq === workQuantity.expenseSeq && obj.workerKbn === workQuantity.workerKbn)

    if (lastMonth === undefined || workQuantity.newAddFlg === 1) {
      diffList = new Array<string>(workQuantity.workQuantityByMonth.length).fill('1')
    } else {
      workQuantity.workQuantityByMonth.forEach((_, index) => {
        // シミュレーション年月度が4月度の場合
        // 変数．前月度労務能力．業者決定物件．月別労務数のIndexを+12する
        if (simulatedMonth === 4) {
          if (index + 12 <= workQuantity.workQuantityByMonth.length - 1) {
            diffList.push((workQuantity.workQuantityByMonth[index] ?? 0) !== (lastMonth?.workQuantityByMonth[index + 12] ?? 0) ? '1' : '0')
          } else {
            diffList.push('1')
          }
        } else {
          diffList.push((workQuantity.workQuantityByMonth[index] ?? 0) !== (lastMonth?.workQuantityByMonth[index] ?? 0) ? '1' : '0')
        }
      })
    }
    return diffList.join(',')
  }

  // #region useEffect functions
  useEffect(() => {
    // データストアチェック．業者ヒアリング遷移パラメータ．パラメータが設定されていない場合、エラーページへ遷移する
    const isPageError =
        regStatus === undefined ||
        regStatus === null ||
        simulationYm === '' ||
        supplierCd === '' ||
        factoryCd === '' ||
        expenseCd === '' ||
        replyYm === '' ||
        expense === '' ||
        supplierNm === '' ||
        branchCd === '' ||
        branchNm === '' ||
        supplierBranchCd === ''

    if (isPageError) {
      const pageParam = {
        regStatus,
        simulationYm,
        supplierCd,
        factoryCd,
        expenseCd,
        replyYm,
        expense,
        supplierNm,
        branchCd,
        branchNm,
        supplierBranchCd
      }
      throw new Error(`PageError Occurred. ${JSON.stringify(pageParam)}`)
    }
  }, [
    regStatus,
    simulationYm,
    supplierCd,
    factoryCd,
    expenseCd,
    replyYm,
    expense,
    supplierNm,
    branchCd,
    branchNm,
    supplierBranchCd
  ])

  useEffect(() => {
    fetchHearingQuantityList()
    return () => { dispatch(ConfirmReplyActions.setSupplierDecisionBuildingLocation(0)) }
  }, [])

  useEffect(() => {
    let text
    codeNames.forEach((codeName) => {
      if (codeName.CODE === expenseCd) {
        text = codeName.TXT6
      }
    })
    setIsAdditionalBuildingGridShow(text === '1')
  }, [codeNames, expenseCd])

  useEffect(() => {
    // 業者決定物件のグリッドデータ作成
    setDecidedBuildingGridDataSate(
      produce((draft) => {
        const workQuantityList = decidedHearingQuantityListRes.workQuantityList
        const workQuantityListLastMonth = decidedHearingQuantityListResLastMonth.workQuantityList

        const formatedWorkQuantityList = workQuantityList.map((item) => ({
          ...item,
          delFlg: 0
        }))

        draft.grid = createData(
          formatedWorkQuantityList,
          simulationYm,
          nonCtrlFlg,
          true,
          displayStartYear,
          userProp?.authority
        )
        draft.workQuantityList = formatedWorkQuantityList
        draft.workQuantityListLastMonth = workQuantityListLastMonth

        const displayModes = workQuantityList.map((obj) => {
          return obj.hiddenFlg === 0 && obj.lostOrderFlg === 0
        })
        draft.displayModes = displayModes
      })
    )

    // 業者追加物件のグリッドデータ作成
    setAdditionalBuildingGridDataSate(
      produce((draft) => {
        const workQuantityList = additionalHearingQuantityListRes.workQuantityList
        const workQuantityListLastMonth = additionalHearingQuantityListResLastMonth.workQuantityList

        const formatedWorkQuantityList = workQuantityList.map((item) => ({
          ...item,
          delFlg: 0
        }))

        draft.grid = createData(
          formatedWorkQuantityList,
          simulationYm,
          nonCtrlFlg,
          false,
          displayStartYear,
          userProp?.authority
        )
        draft.workQuantityList = formatedWorkQuantityList
        draft.workQuantityListLastMonth = workQuantityListLastMonth

        const displayModes = workQuantityList.map((obj) => {
          return obj.hiddenFlg === 0 && obj.lostOrderFlg === 0
        })
        draft.displayModes = displayModes
      })
    )

    // 見積依頼物件のグリッドデータ作成
    setQuotationRequestBuildingGridDataSate(
      produce((draft) => {
        const workQuantityList = quotationRequestHearingQuantityListRes.workQuantityList
        const workQuantityListLastMonth = quotationRequestHearingQuantityListResLastMonth.workQuantityList

        const formatedWorkQuantityList = workQuantityList.map((item) => ({
          ...item,
          delFlg: 0
        }))

        draft.workQuantityList = formatedWorkQuantityList
        draft.workQuantityListLastMonth = workQuantityListLastMonth
        draft.grid = createData(
          formatedWorkQuantityList,
          simulationYm,
          nonCtrlFlg,
          false,
          displayStartYear,
          userProp?.authority
        )

        const displayModes = workQuantityList.map((obj) => {
          return obj.hiddenFlg === 0 && obj.lostOrderFlg === 0
        })
        draft.displayModes = displayModes

        setUserAuthority(
          userProp === undefined ? Authority.None : userProp.authority
        )
      })
    )
  }, [decidedHearingQuantityListRes.workQuantityList,
    additionalHearingQuantityListRes.workQuantityList,
    quotationRequestHearingQuantityListRes.workQuantityList,
    decidedHearingQuantityListResLastMonth.workQuantityList,
    additionalHearingQuantityListResLastMonth.workQuantityList,
    quotationRequestHearingQuantityListResLastMonth.workQuantityList,
    simulationYm])

  // #endregion useEffect functions

  return (
    <>
      <SupplierHearingHeader
        regStatus={decidedHearingQuantityListRes.addStsCd}
        promptInfo={hearingMessage.buildingDecidedBySupplier.headerPromptInfo}
        enableFixContentRegistration
        supplierHeaderInfo={{
          replyYm,
          supplierNm,
          expense,
          branchNm,
          factoryNm,
          supplierBranchNm
        }}
      />

      <BuildingDecidedBySupplierGrid
        years={YEARS}
        months={MONTHS}
        simulationYm={simulationYm}
        displayStartYear={displayStartYear}
        expenseCd={expenseCd}
        gridDataState={decidedBuildingGridDataSate}
        nonCtrlFlg={nonCtrlFlg}
        onCellsChanged={handleDecidedCellsChanged}
        onBuildingInfoBtnClick={handleBuildingInfoBtnClick}
        onDecidedDisplayCheckboxChange={handleOnDecidedDisplayCheckboxChange}
        onDecidedApplicabCheckboxChange={handleOnDecidedApplicabCheckboxChange}
        onConstPartDropDownChange={handleOnConstPartDropDownChange}
        onPrssStsDropDownChange={handleOnPrssStsDropDownChange}
        onDelayLevelCdDropDownChange={handleOnDelayLevelCdDropDownChange}
        onWorkerKbnDropDownChange={handleOnWorkerKbnDropDownChange}
        onDuplicationBtnClick={handleDuplicationBtnClick}
        onUndoDuplicationBtnClick={handleUndoDuplicationBtnClick}
      />

      {isAdditionalBuildingGridShow && (
        <AdditionalBuildingGrid
          years={YEARS}
          months={MONTHS}
          simulationYm={simulationYm}
          displayStartYear={displayStartYear}
          expenseCd={expenseCd}
          gridDataState={additionalBuildingGridDataSate}
          nonCtrlFlg={nonCtrlFlg}
          onCellsChanged={handleAdditionalCellsChanged}
          onAddRowBtnClick={handleOnAddRowBtnClick}
          onAdditionalDisplayCheckboxChange={handleOnAdditionalDisplayCheckboxChange}
          onAdditionalApplicabCheckboxChange={handleOnAdditionalApplicabCheckboxChange}
          onDeleteCheckboxChange={handleOnDeleteCheckboxChange}
          onBranchDropDownChange={handleOnBranchDropDownChange}
          onAddBuildingInfoChange={handleOnAddBuildingInfoChange}
        />
      )}

      <QuotationRequestBuildingGrid
        gridDataState={quotationRequestBuildingGridDataSate}
        years={YEARS}
        months={MONTHS}
        simulationYm={simulationYm}
        displayStartYear={displayStartYear}
        nonCtrlFlg={nonCtrlFlg}
        userAuthority={userAuthority}
        onDisplayCheckboxChange={handleOnQuotationRequestDisplayCheckboxChange}
        onWorkQuantityBtnClick={handleBuildingInfoBtnClick}
        expenseCd={expenseCd}
        onCellsChanged={handleQuotationRequestCellsChanged}
        onRemarkInputChange={handleOnRemarkInputChange}
      />

      <BuildingDecidedBySupplirFooter
        displayMode={displayMode}
        nonCtrlFlg={nonCtrlFlg}
        onHiddenBtnClick={handleOnHiddenBtnClick}
        onTempSaveBtnClick={handleOnTempSaveBtnClick}
        onSaveBtnClick={handleOnSaveBtnClick}
      />

      {buildingInformationWindowShow && (
        <BuildingInformation
          onClose={handleOnCloseBuildingInfoWindow}
          buildingInfoParams={buildingInfoParams}
        />
      )}

      {confirmDialogSignInShow && (
        <ConfirmDialog
          title="登録"
          open={confirmDialogSignInShow}
          renderCloseButton
          id={'I007'}
          args={['登録']}
          onClickOk={dialogConfirmSignInBtn}
          onClickCancel={() => setConfirmDialogSignInShow(false)}
        />
      )}
      {confirmDialogTempSaveShow && (
        <ConfirmDialog
          title="一時保存"
          open={confirmDialogTempSaveShow}
          renderCloseButton
          id={'I007'}
          args={['一時保存']}
          onClickOk={dialogConfirmTempSaveBtn}
          onClickCancel={() => setConfirmDialogTempSaveShow(false)}
        />
      )}
      <ErrorMessages type="notification" messages={notificationMessages} />
      <ErrorMessages type="dialog" messages={errorMessages} />
    </>
  )
}

/**
 * GridDataの初期化
 * @param workQuantityList
 * @param simulationYm
 * @param nonCtrlFlg
 * @returns
 */
const createData = (
  workQuantityList: WorkQuantity[],
  simulationYm: string,
  nonCtrlFlg: boolean,
  decision: boolean, // 業者決定物件の場合true,業者追加物件の場合false
  displayStartYear: string,
  authority?: Authority
) => {
  const gridData: GridElement[][] = []

  // 労務数一覧が空の場合、合計行のみを作成する
  const totalCount = decision ? 3 : 1
  if (workQuantityList.length === 0) {
    for (let arr = 0; arr < totalCount; arr++) {
      gridData.push([])
      for (let i = 0; i < YEARS * MONTHS.length; i++) {
        gridData[arr][i] = {
          value: 0,
          readOnly: true
        }
      }
    }
    return gridData
  }

  for (let r = 0; r < workQuantityList.length; r++) {
    gridData[r] = []
    for (let fy = 0; fy < YEARS; fy++) {
      for (let m = 0; m < MONTHS.length; m++) {
        gridData[r][fy * 12 + m] = {
          value: null,
          readOnly: onDataModifiableFn((fy * 12 + m), simulationYm, displayStartYear, authority) || nonCtrlFlg || (workQuantityList[r].lostOrderFlg === 1 && !(authority === Authority.Builder || authority === Authority.Material || authority === Authority.Branch))
        }
      }
    }
  }

  workQuantityList.forEach((workQuantityObj, rowIndex) => {
    const workQuantityByMonthArr = workQuantityObj.workQuantityByMonth
    workQuantityByMonthArr.forEach((valByMonth, columnIndex) => {
      gridData[rowIndex][columnIndex].value = valByMonth
    })
  })

  // 合計の計算
  for (let arr = 1; arr <= totalCount; arr++) {
  // 合計のRowを追加
    gridData.push([])

    // 合計の計算
    for (let i = 0; i < gridData[0].length; i++) {
      let sum = 0
      for (let j = 0; j < gridData.length - arr; j++) {
        if ((!decision || // 業者追加物件の場合は全件の合計を算出
            (arr === 1 && workQuantityList[j].workerKbn === '1') || // 業者追加物件の場合は、1回目のループの場合は直手のみの合計を算出
            (arr === 2 && workQuantityList[j].workerKbn === '2') || // 2回目のループの場合は、応援のみの合計を算出。3回目のループ時は全件の合計を算出
            (arr === 3)) &&
            workQuantityList[j].completedFlg !== 1) {
          sum += Number(gridData[j][i].value) ?? 0
        }
      }
      gridData[gridData.length - 1][i] = {
        value: sum,
        readOnly: true
      }
    }
  }
  return gridData
}

/**
 * @description 合計の計算
 * @param draft WritableDraft<GridDataSate>
 * @param currentRowIndex number
 * @param lastRowIndex number
 */
const setTotalRowData = (
  draft: WritableDraft<GridDataSate>,
  currentRowIndex: number,
  lastRowIndex: number,
  decision: boolean // 業者決定物件の場合true、業者追加物件の場合false
) => {
  for (let i = 0; i < draft.grid[lastRowIndex].length; i++) {
    if (currentRowIndex === 0) {
      // 総合計
      draft.grid[lastRowIndex][i].value = 0
      // 業者決定物件の場合のみ
      if (decision) {
        // 直手小計
        draft.grid[lastRowIndex - 1][i].value = 0
        // 応援小計
        draft.grid[lastRowIndex - 2][i].value = 0
      }
    }

    if (draft.workQuantityList[currentRowIndex].completedFlg !== 1) {
      // 総合計
      draft.grid[lastRowIndex][i].value =
        Number(draft.grid[lastRowIndex][i].value) +
        Number(draft.grid[currentRowIndex][i].value)
      if (decision) {
        // 直手小計
        if (draft.workQuantityList[currentRowIndex].workerKbn === '1') {
          draft.grid[lastRowIndex - 2][i].value =
        Number(draft.grid[lastRowIndex - 2][i].value) +
        Number(draft.grid[currentRowIndex][i].value)
        }
        // 応援小計
        if (draft.workQuantityList[currentRowIndex].workerKbn === '2') {
          draft.grid[lastRowIndex - 1][i].value =
        Number(draft.grid[lastRowIndex - 1][i].value) +
        Number(draft.grid[currentRowIndex][i].value)
        }
      }
    }
  }
}

// カレンダの活性状態を設定
const onDataModifiableFn = (i: number, simulationYm: string, displayStartYear: string, authority?: Authority) => {
  const simulatedYear = Number(simulationYm.substring(0, 4))
  const simulatedMonth = Number(simulationYm.substring(4, simulationYm.length))
  const year = new Date().getFullYear()
  const month = new Date().getMonth() + 1

  // シミュレーション年月度が過去の場合すべて非活性
  if (simulatedYear < year ||
      (simulatedYear === year && simulatedMonth < month)) {
    return true
  }

  // 過去月を非活性
  // 社内ユーザは過去月も入力可能とする
  if (authority === Authority.Builder || authority === Authority.Material || authority === Authority.Branch) {
    return false
  } else {
    // 表示開始年度から当年までの差分年数を算出する（年度は4月開始）
    let diffYear = year - Number(displayStartYear)
    if (month < 4) {
      // 1～3月は年度の10～12か月目（+9）に変換し、0始まりの配列位置にするため-1する
      diffYear = diffYear - 1
      return i < month + 9 - 1 + diffYear * 12
    } else {
      // 4月以降は4月を年度の1か月目（-3）として、0始まりの配列位置にするため-1する
      return i < month - 3 - 1 + diffYear * 12
    }
  }
}

/**
 * 前月度の取得
 * @param year
 * @param month
 * @returns 前月度
 */
const getPreMonth = (year: number, month: number) => {
  let year2 = year
  let month2 = month - 1
  if (month2 === 0) {
    year2 = year2 - 1
    month2 = 12
  }

  let month2Str = `${month2}`
  if (month2 < 10) {
    month2Str = `0${month2}`
  }
  return `${year2}${month2Str}`
}
